import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getTiposHallazgos, updateTiposHallazgos } from 'actions/configuracionCatalogos.action';
import { filterList } from 'utils/filterList';
import { generateId } from 'utils/idGenerator';
import { useDebounce } from 'hooks/useDebounce';
import { downloadExcelConfiguracionCatalogos } from 'actions/files.actions';
import { base64toBlob } from 'utils/file';
import { Tooltip } from 'antd';
import {
  MEDIOS_IMPRESOS_SELECTOR,
  TIPOS_MEDIOS_IMPRESOS
} from 'constants/configuracionTiposCatalogos';
import { Table, Button, SelectTamanoPagina, Input } from 'components';
import Editar from 'assets/images/Edit/edit_icon.svg';
import Eliminar from 'assets/images/DeleteForever/delete_forever.svg';
import ModalEliminarRegistro from '../../../components/ModalEliminarRegistro/ModalEliminarRegistro';
import ModalRegistroTipoMI from '../ModalRegistroTipoMediosImpresos/ModalRegistroTipoMI';
import './TablaTipoMediosImpresos.scss';

const DEBOUNCE_TIME_FILTER = 1000;
export const TablaTipoMediosImpresos = () => {
  const filtersRef = useRef({});
  const dispatch = useDispatch();
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [filters, setFilters] = useState({});
  const [openModalRegistro, setOpenModalRegistro] = useState(false);
  const [isOpenEliminar, setIsOpenEliminar] = useState(false);
  const [registroEliminar, setRegistroEliminar] = useState(null);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [data, setData] = useState(null);
  const [listaAtributos, setListaAtributos] = useState(null);
  const applyFilter = useDebounce((fields) => setFilters(fields), DEBOUNCE_TIME_FILTER);

  const showTotal = (total, range) => {
    return `Mostrando del ${range[0]} al ${range[1]} de ${total} registros`;
  };

  // console.log('data', data);

  const [pagination, setPagination] = useState({
    position: ['bottomRight'],
    showQuickJumper: true,
    size: 'small',
    pageSize: 10,
    current: 1,
    showTotal,
    total: 0,
    showSizeChanger: false
  });

  const checkUniqueElement = (nameElement) => {
    return data.some(
      (d) => d.nombreHallazgo === nameElement.trim() && d.idHallazgo !== selectedRow?.idHallazgo
    );
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    const payload = {
      idTipoCatalogo: TIPOS_MEDIOS_IMPRESOS,
      hallazgos: [...data]
    };
    const { success, response } = await dispatch(downloadExcelConfiguracionCatalogos(payload));
    if (response !== null && response !== undefined && success) {
      if (response.data.code === 200) {
        if (response.data.excel64) {
          const url = window.URL.createObjectURL(
            base64toBlob(response.data.excel64.base64, response.data.excel64.tipo)
          );
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', response.data.excel64.nombre);
          link.click();
          setDownloadingExcel(false);
        }
      }
    }
    setDownloadingExcel(false);
  };
  const handleChangeFilter = ({ target }) => {
    const { value, name } = target;
    filtersRef.current[name] = value;
    applyFilter({ ...filtersRef.current });
  };

  const filter = (columnName, lblColumn) => (
    <label htmlFor={columnName}>
      {lblColumn}
      <Input
        id={columnName}
        placeholder="Filtrar"
        name={columnName}
        key={columnName}
        onChange={handleChangeFilter}
      />
    </label>
  );

  const handleChangePageSize = (pageSize) => {
    setPagination((prevPagination) => ({ ...prevPagination, pageSize }));
  };

  const handleDeleteMonitoring = (selected) => {
    setIsOpenEliminar(true);
    setRegistroEliminar(selected);
  };

  const handleCancelModalRegistro = () => {
    setOpenModalRegistro(false);
    setSelectedRow(null);
  };

  const getDataCatalogo = useCallback(async () => {
    setLoading(true);
    try {
      const { response } = await dispatch(
        getTiposHallazgos({ idTipoMonitoreo: MEDIOS_IMPRESOS_SELECTOR })
      );
      setListaAtributos(response.atributosGenerales);
      setData(response.tipos);
      setOpenSuccess(false);
    } catch (error) {
      console.log('Ocurrió un error en la petición:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  // console.log('data', listaAtributos);

  useEffect(() => {
    getDataCatalogo();
  }, []); // Se ejecuta solamente al montar el componente

  const handleUpdateData = () => {
    getDataCatalogo();
    // console.log('handleUpdateData');
  };
  // console.log('data-petición:', data);

  const cancelEliminar = () => {
    setIsOpenEliminar(false);
    setRegistroEliminar(null);
    setOpenSuccess(false);
  };

  const confirmDelete = async () => {
    const peticion = {
      toCancel: [
        {
          idHallazgo: registroEliminar
        }
      ],
      idTipoMonitoreo: MEDIOS_IMPRESOS_SELECTOR
    };
    const { success } = await dispatch(updateTiposHallazgos(peticion));
    if (success) {
      setOpenSuccess(true);
      setIsOpenEliminar(false);
      setRegistroEliminar(null);
      handleUpdateData();
    }
  };

  const dataList = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];
    return filterList(data, filters).map((item) => ({
      ...item,
      key: item.idTipoMedida
    }));
  }, [data, filters]);

  const renderActions = (_, row) => (
    <div className="actions-container">
      <Tooltip title="Editar" className="tooltip-acciones btn-deleteoutlined" placement="topLeft">
        <Button
          type="primary"
          onClick={() => {
            setOpenModalRegistro(true);
            setSelectedRow(row);
            // console.log('Click en editar', row);
          }}
          icon={<img src={Editar} alt="Editar" />}
          ghost
        />
      </Tooltip>

      <Tooltip
        title={
          row.asignado === 1
            ? 'No es posible eliminar el registro, ya que cuenta con monitoreos asignados.'
            : 'Eliminar'
        }
        className="tooltip-acciones btn-deleteoutlined"
        placement="topLeft">
        <Button
          shape="circle"
          className="btnTblAccion"
          icon={<img src={Eliminar} alt="Eliminar" />}
          onClick={() => {
            handleDeleteMonitoring(row.idHallazgo);
            // console.log('Click en Eliminar');
          }}
          disabled={row.asignado === 1}
        />
      </Tooltip>
    </div>
  );

  const columns = [
    {
      title: 'ID del catálogo',
      dataIndex: 'idHallazgo',
      width: '8%',
      key: 'idHallazgo'
    },
    {
      title: filter('nombreHallazgo', 'Nombre del tipo de Medio impreso'),
      dataIndex: 'nombreHallazgo',
      width: '15%',
      key: 'nombreHallazgo'
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      width: '15%',
      key: 'descripcion'
    },
    {
      title: filter('desc_estatus', 'Estatus'),
      dataIndex: 'desc_estatus',
      width: '15%',
      key: 'desc_estatus'
    },
    {
      title: 'Acciones',
      dataIndex: 'actions',
      align: 'center',
      width: '8%',
      key: 'actions',
      render: renderActions
    }
  ];

  return (
    <div className="tabla-tipo-medios-impresos">
      <div className="table-header">
        <div>
          <SelectTamanoPagina
            value={pagination.pageSize}
            onChange={handleChangePageSize}
            disableBottomExcel={downloadingExcel}
            onClick={handleDownloadExcel}
          />
        </div>
        <Button
          text="Agregar tipo de Medios impresos"
          type="primary"
          onClick={() => {
            setOpenModalRegistro(true);
            setSelectedRow(null);
          }}
        />
      </div>
      <Table
        bordered={false}
        columns={columns}
        dataSource={dataList}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize: pageSize || prev.pageSize
            }));
          }
        }}
        loading={loading}
        rowKey={generateId()}
      />
      <ModalEliminarRegistro
        open={isOpenEliminar}
        handleCerrar={cancelEliminar}
        openSuccess={openSuccess}
        confirmDelete={confirmDelete}
        destroyOnClose
      />
      <ModalRegistroTipoMI
        open={openModalRegistro}
        onCancel={handleCancelModalRegistro}
        handleUpdateData={handleUpdateData}
        initialData={selectedRow}
        arrayValues={selectedRow?.atributos?.split(',').map(Number)}
        checkUniqueElement={checkUniqueElement}
        listaAtributos={listaAtributos}
        destroyOnClose
      />
    </div>
  );
};
